import { api } from '../lib/api';

/**
 * An invitation from the Slate team to somebody who has never used Slate,
 * comped: the paid plan for them alone, or a team of their own with seats
 * on it. The account already exists when the email arrives; the link on
 * /start/:token signs the reader into it once. See slate-api
 * src/routes/comp-invites.ts.
 */
export interface CompInviteDetails {
  kind: 'user' | 'team';
  /** The address it was sent to, and the account it signs in to. */
  email: string;
  name: string | null;
  /** The team made for them. Null on a person's invitation. */
  team: { name: string; seats: number } | null;
  /** When the grant lapses; null for one that never does. */
  until: string | null;
  /** Whether the plan is still on. It can lapse while the email sits unread. */
  active: boolean;
  expiresAt: string;
  /** Whether the code in the email would still sign them in. */
  codeUsable: boolean;
  /** They have been in already: sign in the ordinary way from here. */
  signedIn: boolean;
}

export function fetchCompInvite(token: string): Promise<CompInviteDetails> {
  return api<CompInviteDetails>(`/v1/comp-invites/${token}`, { auth: 'none' });
}

/** Trade the code from the email for a Firebase sign-in. Works once. */
export function exchangeCompInviteCode(token: string, code: string): Promise<{ customToken: string; email: string }> {
  return api<{ customToken: string; email: string }>(`/v1/comp-invites/${token}/signin-code`, {
    method: 'POST',
    body: { code },
    auth: 'none',
  });
}

/** Send the email again, with a fresh code. Goes to the invited address. */
export function resendCompInvite(token: string): Promise<{ sent: boolean; email: string }> {
  return api<{ sent: boolean; email: string }>(`/v1/comp-invites/${token}/resend`, { method: 'POST', auth: 'none' });
}
